/* eslint-disable camelcase */
import Intercom from "@intercom/messenger-js-sdk";
import QueryString from "query-string";
import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import api from "../../utils/api";
import { generateHmac } from "../../utils/crypto";
import {
  allFeedAccess,
  overwritePerms,
  populateDefLocation,
  populateList,
} from "../../utils/helpers";
import { errorAlert, successAlert } from "../Alerts/actions";
import { setFeedFilter, updateFormValues } from "../Feed/actions";
import {
  setActiveLocation,
  setActiveSurvey,
  setUserLocations,
} from "../SlickCareProfiles/actions";
import actions from "./actions";
import {
  CHECK,
  FORGOT_PASSWORD,
  GET_SUBLIST,
  GET_USERDATA,
  LOGIN,
  LOGOUT,
  RESET_PASSWORD,
  SENT_RESET_PASSWORD,
  SET_PASSWORD,
  UPDATE_USER,
} from "./constants";
import { selectToken } from "./selectors";
const {
  checkFailure,
  checkSuccess,
  loginFailure,
  loginSuccess,
  logoutFailure,
  sentResetLinkSuccess,
  sentResetLinkFailure,
  forgotPasswordSuccess,
  forgotPasswordFailure,
  resetPasswordSuccess,
  resetPasswordFailure,
  getUserDataSuccess,
  getUserDataFailure,
  getSubList: getSubListAction,
  getSubListSuccess,
  getSubListFailure,
  updateUserSuccess,
  updateUserFailure,
  setPasswordSuccess,
  setPasswordFailure,
  updateAllFeedAccess,
} = actions;

export function* checkAuth({ token, history }) {
  if (token) {
    try {
      const userInfo = yield call(api(token).post, `api/employee/me`);
      const activeLocation = yield select(
        (state) => state?.SlickCareProfilesReducer?.activeLocation
      );

      if (userInfo instanceof Error) throw userInfo;

      let user = overwritePerms(userInfo.data);
      let locationList = populateList(user);
      let location = populateDefLocation(user);
      yield put(setUserLocations(locationList ?? []));
      if (activeLocation?.id === undefined) {
        yield put(setActiveLocation(location ?? {}));
      }
      yield put(setActiveSurvey(location ?? {}));
      yield put(getSubListAction());
      yield put(checkSuccess({ token, user: user }));

      let hasAllFeedAccess = allFeedAccess(user?.location);
      yield put(updateAllFeedAccess(hasAllFeedAccess));
      if (!hasAllFeedAccess) {
        yield put(
          setFeedFilter({
            target: "location",
            page: 1,
            limit: 10,
          })
        );
      }

      if (history.location.pathname === "/")
        history.replace(
          history.location.state && history.location.state.referrer
            ? history.location.state.referrer.pathname
            : `/d/home`
        );
    } catch (e) {
      yield put(checkFailure(e));
      localStorage.clear();
      if (history.location.pathname !== "/") history.replace("/");
    }
  } else yield put(checkFailure("Invalid token"));
}

export function* updateUserData({ onUpdateSuccess }) {
  const token = yield select(selectToken);
  try {
    const userInfo = yield call(api(token).post, `api/employee/me`);
    if (userInfo instanceof Error) throw userInfo;
    let locationList = populateList(userInfo?.data);
    let location = populateDefLocation(userInfo?.data);
    yield put(setUserLocations(locationList ?? []));
    yield put(setActiveLocation(location ?? {}));
    yield put(setActiveSurvey(location ?? {}));
    yield put(getSubListAction());
    yield put(updateUserSuccess({ token, user: userInfo.data }));
    typeof onUpdateSuccess === "function" && onUpdateSuccess();
  } catch (e) {
    yield put(updateUserFailure(e));
  }
}

export function* login({ credentials, history }) {
  try {
    const loginResponse = yield call(
      api(null, null, true).post,
      `/api/employee/login`,
      credentials
    );
    const { access_token } = loginResponse.data.data;

    if (access_token) {
      const userInfoResponse = yield call(
        api(access_token, null, true).post,
        `/api/employee/me`
      );

      let user = overwritePerms(userInfoResponse?.data?.data);
      let locationList = populateList(user);
      let location = populateDefLocation(user);
      yield put(setUserLocations(locationList ?? []));
      yield put(setActiveLocation(location ?? {}));
      yield put(setActiveSurvey(location ?? {}));
      yield put(loginSuccess({ token: access_token, user: user }));
      localStorage.setItem("token", access_token);
      yield put(getSubListAction());

      let hasAllFeedAccess = allFeedAccess(user?.location);
      yield put(updateAllFeedAccess(hasAllFeedAccess));
      if (!hasAllFeedAccess) {
        yield put(
          setFeedFilter({
            target: "location",
            page: 1,
            limit: 10,
          })
        );
      }

      if (user?.id) {
        Intercom({
          app_id: process.env.REACT_APP_INTERCOM_ID,
          email: user?.email,
          user_id: user?.id,
          user_hash: generateHmac(user?.id, process.env.REACT_APP_HMAC_SECRET),
          name: `${user?.first_name} ${user?.last_name}`,
          created_at: Date.now(),
        });
      }

      history.push(
        history.location.state &&
          history.location.state.referrer &&
          history.location.state.referrer.pathname !== "/"
          ? history.location.state.referrer.pathname
          : `/d/home`
      );
    }
  } catch (e) {
    yield put(loginFailure(e));
    yield put(errorAlert(e?.response?.data?.message));
    localStorage.clear();
    if (history.location.pathname !== "/") history.replace("/");
  }
}

export function* logout({ userInfo }) {
  try {
    const token = yield select(selectToken);
    yield call(api(token).post, `/api/logout`);
    localStorage.clear();
    // shutdown();
    // eslint-disable-next-line no-restricted-globals
    if (location) location.replace("/");
  } catch (e) {
    yield put(logoutFailure(e));
    localStorage.clear();
    // eslint-disable-next-line no-restricted-globals
    if (location) location.replace("/");
  }
}

export function* sentResetLink({ payload, history }) {
  try {
    const response = yield call(
      api(null, null, true).post,
      `/auth/password/email`,
      payload
    );
    const data = response.data;
    if (data) {
      successAlert(data.message);
      sentResetLinkSuccess();
      history.push(
        history.location.state &&
          history.location.state.referrer &&
          history.location.state.referrer.pathname !== "/login"
          ? history.location.state.referrer.pathname
          : "/dealers"
      );
    } else if (history.location.pathname !== "/login")
      history.replace("/login");
  } catch (e) {
    yield put(sentResetLinkFailure(e));
    yield put(errorAlert("Error while login!"));
    localStorage.clear();
    if (history.location.pathname !== "/login") history.replace("/login");
  }
}

export function* forgotPassword({ credentials, onSuccess }) {
  const token = yield select(selectToken);
  try {
    const response = yield call(
      api(token).post,
      `/api/auth/employee/password/email?from=dev-dash`,
      credentials
    );
    if (response && response?.code == 200) {
      yield put(forgotPasswordSuccess());
      yield put(successAlert(response.message));
      typeof onSuccess === "function" && onSuccess();
    }
  } catch (e) {
    yield put(forgotPasswordFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Error while sending reset link!"));
  }
}

export function* resetPassword({ credentials, history }) {
  const token = yield select(selectToken);
  try {
    const response = yield call(
      api(token).post,
      `/api/auth/employee/password/reset`,
      credentials
    );
    if (response && response?.code == 200) {
      yield put(resetPasswordSuccess());
      yield put(successAlert(response.message));
      if (history) {
        history.push(`/`);
      }
    }
  } catch (e) {
    yield put(resetPasswordFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Error while resetting password!"));
  }
}

export function* getUserData({ payload }) {
  const token = yield select(selectToken);
  let query = payload ? QueryString.stringify(payload) : "";
  try {
    const response = yield call(
      api(token).get,
      `/api/admin-staff-dashboard?${query}`
    );
    if (response && response?.code == 200) {
      yield put(getUserDataSuccess(response?.data));
    }
  } catch (e) {
    // yield put(getUserDataFailure());
    // if (typeof e === "string") yield put(errorAlert(e));
    // else if (typeof e.error === "string") yield put(errorAlert(e.error));
    // else yield put(errorAlert("Error while fetching data"));
  }
}
export function* getSubListData({ payload }) {
  const token = yield select(selectToken);
  try {
    const response = yield call(api(token).get, `/api/item-list`);
    yield put(getSubListSuccess(response?.data));
  } catch (e) {
    yield put(getSubListFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Error while fetching data"));
  }
}
export function* setPasswordData({ credentials, history }) {
  const token = process.env.REACT_APP_CUSTOMER_API_KEY;
  try {
    const response = yield call(
      api(token).post,
      `/api/set-password/${credentials.id}`,
      credentials.data
    );
    yield put(setPasswordSuccess());
    yield put(successAlert(response.message));
    if (history) {
      history.push(`/`);
    }
  } catch (e) {
    yield put(setPasswordFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Error while setting password!"));
  }
}
export function* checkAuthFlow() {
  yield takeLatest(CHECK, checkAuth);
}
export function* loginFlow() {
  yield takeLatest(LOGIN, login);
}
export function* logoutFlow() {
  yield takeLatest(LOGOUT, logout);
}
export function* sentResetLinkFlow() {
  yield takeLatest(SENT_RESET_PASSWORD, sentResetLink);
}
export function* forgotPasswordFlow() {
  yield takeEvery(FORGOT_PASSWORD, forgotPassword);
}
export function* resetPasswordFlow() {
  yield takeEvery(RESET_PASSWORD, resetPassword);
}
export function* getUserDataFlow() {
  yield takeEvery(GET_USERDATA, getUserData);
}
export function* getSubListFlow() {
  yield takeEvery(GET_SUBLIST, getSubListData);
}
export function* updateUserFlow() {
  yield takeEvery(UPDATE_USER, updateUserData);
}
export function* setPasswordFlow() {
  yield takeEvery(SET_PASSWORD, setPasswordData);
}

export default function* rootAuthSaga() {
  yield all([
    loginFlow(),
    checkAuthFlow(),
    logoutFlow(),
    sentResetLinkFlow(),
    forgotPasswordFlow(),
    resetPasswordFlow(),
    getUserDataFlow(),
    getSubListFlow(),
    updateUserFlow(),
    setPasswordFlow(),
  ]);
}
